"use client";

import NavigationArrow from "@/assets/NavigationArrow";
import cn from "classnames";
import useEmblaCarousel, { UseEmblaCarouselType } from "embla-carousel-react";
import Image from "next/image";
import React, { useCallback, useEffect, useState } from "react";

interface EmblaCarouselProps {
  children: React.ReactNode;
  className?: string;
  navigationButtonsType?: 1 | 2;
  navigationButtonClassName?: string;
  carouselClassName?: string;
  hideNavigationButtons?: boolean;
  left_navigation_icon?: string;
  left_navigation_icon_alt?: string;
  right_navigation_icon?: string;
  right_navigation_icon_alt?: string;
  onSlideChange?: (index: number) => void;
}

const EmblaCarousel: React.FC<EmblaCarouselProps> = ({
  children,
  className,
  navigationButtonsType = 1,
  navigationButtonClassName,
  carouselClassName,
  hideNavigationButtons = false,
  // slideFrequency = 0,
  left_navigation_icon,
  left_navigation_icon_alt,
  right_navigation_icon,
  right_navigation_icon_alt,
  onSlideChange,
}) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: "start",
    slidesToScroll: 1, // move by one
    containScroll: "trimSnaps",
    dragFree: false,
  });

  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);

  const onSelect = useCallback(
    (embla: UseEmblaCarouselType) => {
      setCanScrollPrev(embla.canScrollPrev());
      setCanScrollNext(embla.canScrollNext());
      const selectedIndex = embla.selectedScrollSnap();
      onSlideChange?.(selectedIndex);
    },
    [onSlideChange]
  );

  useEffect(() => {
    if (!emblaApi) return;

    onSelect(emblaApi);

    emblaApi.on("select", onSelect);

    return () => {
      emblaApi.off("select", onSelect);
    };
  }, [emblaApi, onSelect]);

  const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);

  return (
    <div className={`relative w-full ${className || ""}`}>
      <div className={cn("overflow-hidden", carouselClassName)} ref={emblaRef}>
        <div className="-mx-2 flex">
          {React.Children.map(children, (child, index) => (
            <div
              key={index}
              className="flex-[0_0_25%] px-2 flex items-center justify-center"
            >
              {child}
            </div>
          ))}
        </div>
      </div>

      {navigationButtonsType === 2 && !hideNavigationButtons && (
        <div className="absolute top-1/2 -translate-y-1/2 flex items-center justify-between w-full">
          <button
            onClick={scrollPrev}
            className={cn(
              "cursor-pointer transition-transform ",
              "xs:size-9",
              "sm:size-[38px]",
              "lg:size-[52px]",
              !canScrollPrev && "opacity-50"
            )}
            aria-label="Previous slide"
          >
            {left_navigation_icon ? (
              <Image
                src={left_navigation_icon || ""}
                alt={left_navigation_icon_alt || "Previous"}
                width={100}
                height={34}
                className={cn(
                  "w-full h-full object-contain",
                  emblaApi?.canScrollPrev() ? "opacity-100" : "opacity-30"
                )}
              />
            ) : (
              <NavigationArrow
                className={cn(
                  "rotate-180",
                  "xs:size-9",
                  "sm:size-[38px]",
                  "lg:size-[52px]"
                )}
                fill="#f74958"
                pathFill="#F8F9FA"
              />
            )}
          </button>
          <button
            onClick={scrollNext}
            className={cn(
              "cursor-pointer transition-transform ",
              "xs:size-9",
              "sm:size-[38px]",
              "lg:size-[52px]",
              !canScrollNext && "opacity-50"
            )}
            aria-label="Next slide"
          >
            {right_navigation_icon ? (
              <Image
                src={right_navigation_icon || ""}
                alt={right_navigation_icon_alt || "Next"}
                width={100}
                height={100}
                className={cn(
                  " w-full h-full object-contain",
                  emblaApi?.canScrollNext() ? "opacity-100" : "opacity-30"
                )}
              />
            ) : (
              <NavigationArrow
                className={cn("xs:size-9 ", "sm:size-[38px]", "lg:size-[52px]")}
                fill="#f74958"
                pathFill="#F8F9FA"
              />
            )}
          </button>
        </div>
      )}
    </div>
  );
};

export default EmblaCarousel;
